import BaseChart from '@components/charts/BaseChart';
import type { ChartData } from 'types/common';

interface Props {
  colors: readonly string[];
  compact?: boolean;
  data: ChartData[];
  gridHorizontal?: boolean;
  gridVertical?: boolean;
  width?: number;
  height?: number;
  onSelectDataPoint?: (data: ChartData, index?: number) => void;
}

function IndexChart(props: Props) {
  // The average value is fixed (100)
  const average = 100;

  // Adjust data so that each value is relative to the average
  const adjustedData = props.data.map((entry) => ({
    ...entry,
    value: entry.value - average
  }));

  // Find the maximum adjusted value to set the range for the x-axis
  const maxAdjustedValue = Math.max(
    ...adjustedData.map(({ value }) => Math.abs(value))
  );

  // Define the number of ticks
  const tickCount = 10;

  // Calculate the tick step
  const tickStep = Math.ceil(maxAdjustedValue / (tickCount / 2) / 10) * 10;

  // Generate the ticks symmetrically around the average value
  const xAxisTicks = Array.from(
    { length: tickCount + 1 },
    (_, index) => (index - Math.floor(tickCount / 2)) * tickStep
  );

  // Set the range of values for the x-axis to include min and max
  const domain: [number, number] = [xAxisTicks.at(0), xAxisTicks.at(-1)];

  // Format the ticks to ensure they are rounded to the nearest ten
  const formatTick = (tick: number, index: number) => {
    if (tick === 0) {
      return 'Avg';
    }

    if (index % 2 === 0) {
      return '';
    }

    return (Math.ceil((tick + average) / 10) * 10).toString();
  };

  const { compact, ...rest } = props;

  return (
    <BaseChart
      {...rest}
      className={compact ? 'compact' : undefined}
      data={adjustedData}
      average={average}
      domain={domain}
      xAxisTicks={xAxisTicks}
      formatLabel={(value) => Math.round(Number(value))}
      formatTick={formatTick}
    />
  );
}

export default IndexChart;
